import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { useMemo, useRef, type KeyboardEvent } from "react";
import { resolveAdvisorCopy, SELECTABLE_ADVISORS } from "./advisor.data";
import { AdvisorChip } from "./components/AdvisorChip";
import type { IAdvisor } from "./types";

interface AdvisorQuickPickerProps {
  advisor: IAdvisor;
  onSelect: (slug: string) => void;
  /** Resolves the row copy (My Data Only vs Company Only, etc.) for the
   *  employee-facing surface. */
  isPersonal?: boolean;
  className?: string;
}

/**
 * Advisor row for the greeting screen. The composer picker is the permanent
 * control; this exists so a first-time user meets the advisors before typing,
 * instead of discovering an icon by accident.
 */
export const AdvisorQuickPicker = ({
  advisor,
  onSelect,
  isPersonal = false,
  className,
}: AdvisorQuickPickerProps) => {
  const { t } = useTranslation(["common"]);
  const groupRef = useRef<HTMLDivElement>(null);
  const options = useMemo(
    () => SELECTABLE_ADVISORS.map((a) => resolveAdvisorCopy(a, isPersonal)),
    [isPersonal],
  );
  const activeIndex = Math.max(
    0,
    options.findIndex((a) => a.slug === advisor.slug),
  );

  // Arrow keys move between chips and select as they go — the pattern a radio
  // group is expected to follow.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;

    event.preventDefault();
    const total = options.length;
    const nextIndex = (activeIndex + step + total) % total;
    onSelect(options[nextIndex].slug);
    groupRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
      .item(nextIndex)
      ?.focus();
  };

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col items-center gap-2",
        className,
      )}
    >
      {/* The chips carry their own meaning — an "Answering as" caption on top of
          a checked-state row is a third explanation of the same thing. Kept for
          screen readers, where the group does need naming. */}
      <span id="advisor-quick-picker-label" className="sr-only">
        {t("common:ai_assistant.answering_as")}
      </span>

      <div
        ref={groupRef}
        role="radiogroup"
        aria-labelledby="advisor-quick-picker-label"
        // Phones scroll the row sideways rather than stacking it three rows
        // deep above the composer; from sm up it wraps and centres as before.
        className={cn(
          "scrollbar-hidden flex w-full items-center gap-2 overflow-x-auto px-1 pb-1",
          "max-sm:snap-x max-sm:snap-mandatory",
          "sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0 sm:pb-0",
        )}
      >
        {options.map((option, index) => (
          <AdvisorChip
            key={option.slug}
            advisor={option}
            isActive={option.slug === advisor.slug}
            onSelect={onSelect}
            tabIndex={index === activeIndex ? 0 : -1}
            onKeyDown={handleKeyDown}
          />
        ))}
      </div>

      {/* The one line that explains the selected advisor — no footer needed on
          the landing screen. */}
      <p
        aria-live="polite"
        className="m-0 max-w-[56ch] text-balance px-2 text-center text-xs text-muted-foreground"
      >
        {t(advisor.helper)}
      </p>
    </div>
  );
};
